<template>
  <v-dialog v-model="bindMaterialDialog" max-width="900px">
    <v-card>
      <v-card-title class="ma-3">
        <span v-if="!supplierSelected">
          {{ $t("$vuetify.alert") }}
        </span>
        <span v-else>
          {{
            $t("PurchasesDetail.get_relation_ship.title", [
              supplierSelected ? supplierSelected.supplier_name : "",
              material.material_name,
            ])
          }}
        </span>

        <v-spacer />
        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              @click="bindMaterialDialog = false"
            >
              fa-times
            </v-icon>
          </template>
          <span>{{ $t("$vuetify.close") }}</span>
        </v-tooltip>
      </v-card-title>
      <v-divider />
      <div class="ma-4" v-if="supplierSelected">
        <ValidationObserver ref="observer">
          <v-data-table
            :headers="headers"
            :items="getRelationShip"
            :loading="loading"
            multi-sort
            class="elevation-0 px-6"
            v-resize="resizeHeader"
            :footer-props="footerProps"
          >
            <template v-slot:item.material_category.name="{ item }">
              {{ item.material_category.name }}
            </template>
            <template v-slot:item.supply_unit="{ item }">
              <v-select
                v-model="item.supply_unit"
                :items="item.standard.standard_details"
                item-value="id"
                item-text="unit"
                return-object
                single-line
                dense
                hide-details
                style="max-width: 60px; margin-left: auto"
              ></v-select>
            </template>
            <template v-slot:item.price="{ item }">
              <ValidationProvider
                v-slot="{ errors }"
                :name="$t('PurchasesDetail.get_relation_ship.price')"
                rules="required"
              >
                <v-text-field
                  v-model="item.price"
                  single-line
                  dense
                  type="number"
                  min="0"
                  :prefix="$t('Global.money')"
                  @blur="
                    item.price = $numberFormatter.formatWithDotNoMoreThan(
                      item.price,
                      2
                    )
                  "
                  style="max-width: 80px; margin-left: auto"
                  hide-details
                  :error-messages="errors"
                />
              </ValidationProvider>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                small
                dark
                color="print"
                class="px-2 me-2 mb-1"
                @click="bindMaterial(item)"
              >
                {{ $t("PurchasesDetail.actions.bind") }}
              </v-btn>
            </template>
          </v-data-table>
        </ValidationObserver>
      </div>
      <v-card-text
        v-else
        class="px-4 py-8 text-center subtitle-1 font-weight-bold"
      >
        {{ $t("PurchasesList.intelligent.alert") }}
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          outlined
          class="elevation-1"
          @click="
            bindMaterialDialog = false;
          "
        >
          {{ $t("$vuetify.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import SupplierMaterials from "@/api/supplier_materials";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  name: "SearcherProductSupplier",
  props: {
    value: Boolean,
    material: Object,
    dense: Boolean,
    unclearable: Boolean,
    supplier: Object,
  },

  components: {
    ValidationObserver,
    ValidationProvider,
  },

  computed: {
    headers() {
      var headers = [
        {
          text: i18n.t("PurchasesDetail.get_relation_ship.category"),
          align: "start",
          sortable: false,
          value: "material_category.name",
        },
        {
          text: i18n.t("PurchasesDetail.get_relation_ship.material_name"),
          align: "start",
          sortable: false,
          value: "material_name",
        },
        {
          text: i18n.t("PurchasesDetail.get_relation_ship.material_code"),
          align: "start",
          sortable: false,
          value: "material_code",
        },
      ];
      headers.push(
        {
          text: i18n.t("PurchasesDetail.get_relation_ship.purchase_unit"),
          align: "center",
          sortable: false,
          value: "supply_unit",
        },
        {
          text: i18n.t("PurchasesDetail.get_relation_ship.price"),
          align: "end",
          sortable: false,
          value: "price",
        }
      );
      return headers.concat([
        {
          align: "end",
          sortable: false,
          value: "actions",
        },
      ]);
    },

    footerProps() {
      return Object.assign({}, i18n.t("$config.footer-props"), {
        itemsPerPageOptions: [this.itemsPerPageOptions],
      });
    },

    ...mapState(["loading", "settings"]),
  },

  data: () => ({
    getRelationShip: [],
    suppliers: [],
    supplierSelected: null,
    bindMaterialDialog: false,
    itemsPerPageOptions: 10,
  }),

  methods: {
    resizeHeader() {
      this.itemsPerPageOptions = window.innerHeight > 800 ? 10 : 5;
    },
    getSuppliersFromAPI() {
      SupplierMaterials.fetchRelation({
        material_id: this.material.material_id,
        supplier_id: this.supplierSelected.id,
      }).then(
        function (response) {
          if (response.data.status.isOK()) {
            if (
              response.data.suppliers.length &&
              response.data.suppliers[0].type == 2
            ) {
              this.getRelationShip =
                response.data.suppliers[0].supplier_materials;
            } else {
              this.getRelationShip = [response.data.material];
            }
            // console.log(this.getRelationShip);
          }
        }.bind(this)
      );
    },
    bindMaterial(materialDetail) {
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            if (materialDetail.price) {
              var supplier_material_relation = {
                standard_detail_id: materialDetail.supply_unit.id,
                supply_price: materialDetail.price
                  ? materialDetail.price
                  : materialDetail.in_price,
                supply_code: materialDetail.supply_code
                  ? materialDetail.supply_code
                  : "",
                material_id: materialDetail.id,
              };

              SupplierMaterials.batchSave({
                supplier_id: this.supplierSelected.id,
                supplier_material_relations: [supplier_material_relation],
              }).then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$emit("refresh");
                    this.bindMaterialDialog = false;
                  }
                }.bind(this)
              );
            }
          }
        }.bind(this)
      );
    },
  },

  watch: {
    value: {
      immediate: true, //初始赋值也调用
      handler() {
        this.bindMaterialDialog = this.value;
      },
    },
    supplier: {
      immediate: true,
      handler() {
        this.supplierSelected = this.supplier;
      },
    },
    supplierSelected: {
      handler() {
        if (this.supplierSelected) {
          this.getSuppliersFromAPI();
        }
      },
    },
    bindMaterialDialog: {
      handler() {
        this.$emit("input", this.bindMaterialDialog);
      },
    },
    getRelationShip: {
      handler() {
        if (this.getRelationShip.length) {
          this.getRelationShip.map((detail) => {
            if (!detail.supply_unit) {
              detail.supply_unit = detail.standard.default_standard_detail;
            }

            return detail;
          });
        }
      },
    },
  },
};
</script>
